<template>
  <edit-modal
    :is-show-dialog="isShowDialog"
    :action-label="actionLabel"
    :title="dialogTitle"
    @on-close="onClose"
    @on-action="onAction"
  >
    <v-overlay :absolute="true" :value="isModalLoading">
      <v-progress-circular indeterminate size="24"></v-progress-circular>
    </v-overlay>
    <v-form ref="form" autocomplete="off" method="post" action="">
      <v-container>
        <v-row v-if="allowAddAddress">
          <v-col cols="12" md="3">
            <v-switch
              v-model="editableObject.addAddress"
              class="my-0"
              :label="$t('clients.add_client_address') + '?'"
            />
          </v-col>
        </v-row>
        <v-row v-model="valid" :lazy-validation="true">
          <v-col cols="12" md="3">
            <v-text-field
              v-model="editableObject.name"
              :counter="200"
              :rules="[rules.required, rules.max(200)]"
              :label="lang($store, 'sap/listings.global.filter.hints.name')"
              prepend-icon="mdi-account-badge-outline"
              required
              autocomplete="off"
              :error-messages="errors && errors.name ? errors.name : null"
              @input="removeError('name')"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="5">
            <field-phone-with-note
              v-model="editableObject.phone"
              :value-note="editableObject.phone_note"
              :rules="[rules.required, rules[initPhoneRule]]"
              :rules-note="[rules.max(50)]"
              :counter="50"
              :error-message="errors && errors.phone ? errors.phone : null"
              :error-message-note="
                errors && errors.phone_note ? errors.phone_note : null
              "
              @input-note="editableObject.phone_note = $event"
            ></field-phone-with-note>
          </v-col>
          <v-col cols="12" md="4">
            <field-phone-with-note
              v-model="editableObject.phone2"
              :value-note="editableObject.phone2_note"
              :rules="[rules[initPhoneRule]]"
              :rules-note="[rules.max(50)]"
              :counter="50"
              :label="lang($store, 'sap/listings.global.filter.hints.phone2')"
              :error-message="errors && errors.phone2 ? errors.phone2 : null"
              :error-message-note="
                errors && errors.phone2_note ? errors.phone2_note : null
              "
              @input-note="editableObject.phone2_note = $event"
            ></field-phone-with-note>
          </v-col>

          <v-col cols="12" md="3">
            <v-select
              v-model="editableObject.source"
              :items="maybeWithoutExcluded"
              prepend-icon="mdi-arrow-decision-outline"
              single-line
              :hint="lang($store, 'sap/listings.global.filter.hints.source')"
              persistent-hint
              :error-messages="errors && errors.source ? errors.source : null"
              @change="removeError('source')"
            ></v-select>
          </v-col>
          <v-col cols="12" md="6">
            <v-row class="pt-4">
              <v-col cols="12" md="4">
                <v-switch
                  v-model="editableObject.use_ukrainian"
                  class="my-0"
                  :false-value="0"
                  :true-value="1"
                  :label="
                    lang(
                      $store,
                      'sap/listings.global.filter.hints.use_ukrainian'
                    )
                  "
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="editableObject.use_english"
                  class="my-0"
                  :false-value="0"
                  :true-value="1"
                  :label="
                    lang($store, 'sap/listings.global.filter.hints.use_english')
                  "
                />
              </v-col>
              <v-col cols="12" md="4">
                <v-switch
                  v-model="editableObject.is_viber"
                  class="my-0"
                  :false-value="0"
                  :true-value="1"
                  :label="
                    lang($store, 'sap/listings.global.filter.hints.is_viber')
                  "
                />
              </v-col>
              <v-col cols="12" md="6" v-if="!isCreation">
                <v-switch
                  v-model="editableObject.black_listed"
                  class="my-0"
                  color="#000"
                  :false-value="0"
                  :true-value="1"
                  :label="
                    lang(
                      $store,
                      'sap/listings.global.filter.hints.black_listed'
                    )
                  "
                />
              </v-col>
            </v-row>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="editableObject.note"
              :counter="200"
              :rules="[rules.max(200)]"
              prepend-icon="mdi-tooltip-text-outline"
              :label="lang($store, 'sap/listings.global.filter.hints.note')"
              autocomplete="off"
              :error-messages="errors && errors.note ? errors.note : null"
              @input="removeError('note')"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              v-model="editableObject.password"
              :counter="50"
              :rules="[rules.max(50), rules.min(6)]"
              prepend-icon="mdi-lock-question"
              :append-icon="
                showPass ? 'mdi-eye-outline' : 'mdi-eye-off-outline'
              "
              :type="showPass ? 'text' : 'password'"
              @click:append="showPass = !showPass"
              :label="lang($store, 'sap/listings.global.filter.hints.password')"
              :hint="
                lang(
                  $store,
                  'sap/listings.global.filter.hints.password_live_empty'
                )
              "
              persistent-hint
              autocomplete="new-password"
              :error-messages="
                errors && errors.password ? errors.password : null
              "
              @input="removeError('password')"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-row>
          <v-col cols="12">
            <v-textarea
              v-model="editableObject.additional_info"
              rows="3"
              :counter="3000"
              :rules="[rules.max(3000)]"
              prepend-icon="mdi-account-badge-horizontal-outline"
              :label="
                lang($store, 'sap/listings.global.filter.hints.additional_info')
              "
              autocomplete="off"
              :error-messages="
                errors && errors.additional_info ? errors.additional_info : null
              "
              @input="removeError('additional_info')"
            ></v-textarea>
          </v-col>
        </v-row>

        <v-row v-if="!isCreation">
          <v-col cols="12">
            <h2 class="headline" style="position: relative">
              <v-icon>mdi-home-group</v-icon>
              <span>
                {{ lang($store, 'sap/listings.address.addresses') }}
              </span>
              <v-btn
                icon
                absolute
                right
                style="top: 0"
                @click="$emit('on-add-address', item)"
              >
                <v-icon>mdi-home-plus</v-icon>
              </v-btn>
            </h2>
          </v-col>
          <v-col
            v-for="address in item.addresses"
            :key="address.uuid"
            cols="12"
            sm="6"
            md="4"
            lg="3"
          >
            <v-card outlined class="v-sheet" style="height: 100%">
              <v-card-title>
                <v-icon large left>mdi-home-city-outline </v-icon>
                <span class="title font-weight-light">
                  {{
                    lang(
                      $store,
                      `sap/listings.global.filter.premises.${address.premises_type}`
                    )
                  }}
                </span>
                <v-spacer />
                <hoover v-if="hooverModeEnable() && address.is_hoover" />
                <v-spacer />
                <icon-tooltip
                  v-if="address.is_commerce"
                  icon="mdi-currency-usd"
                  text="Коммерция"
                  color="red"
                />
                <v-spacer />
                <v-btn icon @click="onAddressEdit(item, address)">
                  <v-icon>mdi-arrow-right-circle</v-icon>
                </v-btn>
              </v-card-title>
              <v-card-text class="headline font-weight-bold">
                {{ address.formatted }}
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>
        <v-row v-if="!isCreation">
          <v-col cols="12">
            <h2 class="headline" style="position: relative">
              <v-icon>mdi-qrcode-scan</v-icon>
              <span>
                {{ lang($store, 'sap/listings.global.promocodes') }}
              </span>
            </h2>
          </v-col>
          <v-col cols="12">
            <promocodes-simple-list :contact-uuid="item.uuid" />
          </v-col>
        </v-row>
      </v-container>
    </v-form>
  </edit-modal>
</template>

<script>
/* constants */
import PromocodesSimpleList from '@/components/domains/promocodes/PromocodesSimpleList.vue';
import hoover from '@/components/icons/hoover.vue';
import IconTooltip from '@/components/minialerts/IconTooltip.vue';
import * as sources from '~constants/sources';
/* utils */
import { deepMerge, cloneObj } from '../../utils';
/* mixins */
import editModalMixin from '../../mixins/editModalMixin';
import validationMixin from '../../mixins/validationMixin';
import updatePageContentMixin from '../../mixins/updatePageContentMixin';
import settingsMixin from '@/mixins/settingsMixin';
import phoneMixin from "@/mixins/phoneMixin";
/* components */
import EditModal from './EditModal.vue';
import FieldPhoneWithNote from '../text-fields/FieldPhoneWithNote.vue';
import sourcesFilter from '@/mixins/filters/sourcesFilter';

export default {
  name: 'EditModalContact',
  mixins: [editModalMixin, validationMixin, updatePageContentMixin, settingsMixin, phoneMixin, sourcesFilter],
  components: {
    EditModal,
    FieldPhoneWithNote,
    PromocodesSimpleList,
    hoover,
    IconTooltip
  },
  props: {
    allowAddAddress: {
      type: Boolean,
      default: false
    },
    item: {
      type: Object,
      default: () => {}
    },

    errors: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      /* validation */
      showPass: false,
      /* Data */
      initialEditableObject: {
        name: '',
        phone: '',
        phone2: '',
        phone_note: '',
        phone2_note: '',
        source: 'unset',
        use_ukrainian: 0,
        use_english: 0,
        is_viber: 0,
        black_listed: 0,
      },
      editableObject: {}
    };
  },
  mounted() {
    this.initEditableObject();
  },
  computed: {
    account() {
      return this.$store.getters['account/account'];
    },
    isCreation() {
      return !this.item.name;
    },
    dialogTitle() {
      return !this.isCreation ? this.item.name : '';
    },
    sourcesItems() {
      const items = [];
      Object.keys(sources).forEach(key => {
        items.push({
          text: this.lang(
            this.$store,
            `sap/listings.global.filter.sources.${sources[key]}`
          ),
          value: sources[key]
        });
      });
      return items;
    }
  },
  watch: {
    isShowDialog(newVal) {
      if (newVal) {
        this.initEditableObject();
        this.resetValidation();
      }
    }
  },
  methods: {
    fetchData() {
      this.$forceUpdate();
    },
    initEditableObject() {
      this.editableObject = deepMerge(
        cloneObj(this.initialEditableObject),
        this.item
      );
      this.editableObject.addAddress = this.allowAddAddress;
    },
    resetValidation() {
      if (this.$refs.form) {
        this.$refs.form.resetValidation();
      }
    },
    onAction() {
      if (this.$refs.form.validate()) {
        this.$emit('on-action', this.editableObject);
      }
    },
    removeError(key) {
      if (this.errors && this.errors[key]) {
        this.errors[key] = null;
      }
    },
    onAddressEdit(item, address) {
      this.$emit('edit-address', {
        item,
        address
      });
    },
  }
};
</script>

<style lang="sass" scoped></style>
